import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getOrderById, updateOrderStatus } from '../../services/orderService';
import { Order } from '../../types';
import { motion } from 'framer-motion';
import { format, addDays } from 'date-fns';

const AcceptOrder: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrder();
      // Set default delivery date to 7 days from now
      setDeliveryDate(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
    }
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const data = await getOrderById(orderId);
      setOrder(data);
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  };

 const handleAccept = async () => {
  if (!order || !deliveryDate) {
    alert('Please select a delivery date');
    return;
  }

  setProcessing(true);
  try {
    await updateOrderStatus(
      order.id,
      'accepted',
      new Date(deliveryDate),
      order.buyerId  // Remove order.buyerName - only 4 params
    );
    alert('Order accepted successfully!');
    navigate('/seller/orders');
  } catch (error) {
    console.error('Error accepting order:', error);
    alert('Failed to accept order');
  } finally {
    setProcessing(false);
  }
};
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  const minDate = format(addDays(new Date(), 3), 'yyyy-MM-dd');
  const maxDate = format(addDays(new Date(), 30), 'yyyy-MM-dd');

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <h1 className="text-xl font-bold text-gray-800">Accept Order</h1>
      </div>

      <div className="p-4 max-w-2xl mx-auto">
        {/* Order Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg mb-6"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4">Order Details</h2>
          
          <div className="flex gap-4 mb-4">
            <img
              src={order.artworkImage}
              alt={order.artworkTitle}
              className="w-24 h-24 object-cover rounded-lg"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 text-lg">{order.artworkTitle}</h3>
              <p className="text-gray-600 mb-2">Quantity: {order.quantity}</p>
              <p className="text-2xl font-bold text-primary">₹{order.totalAmount.toLocaleString()}</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="font-semibold text-gray-800 mb-2">Customer Details</p>
            <p className="text-gray-700">{order.buyerName}</p>
            <p className="text-gray-600 text-sm">{order.buyerEmail}</p>
          </div>
        </motion.div>

        {/* Shipping Address */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-lg mb-6"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4">Shipping Address</h2>
          <div className="bg-blue-50 rounded-xl p-4 space-y-2">
            <p className="font-semibold text-gray-800">{order.shippingAddress.name}</p>
            <p className="text-gray-700">{order.shippingAddress.phone}</p>
            <p className="text-gray-700">{order.shippingAddress.street}</p>
            <p className="text-gray-700">
              {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
            </p>
            {order.shippingAddress.landmark && (
              <p className="text-gray-600 text-sm">Landmark: {order.shippingAddress.landmark}</p>
            )}
          </div>
        </motion.div>

        {/* Delivery Date Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 shadow-lg mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="text-primary" size={28} />
            <h2 className="text-xl font-bold text-gray-800">Set Delivery Date</h2>
          </div>

          <p className="text-gray-600 mb-4 text-sm">
            Choose an expected delivery date for this order (minimum 3 days from today)
          </p>

          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            min={minDate}
            max={maxDate}
            className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-lg font-semibold"
          />

          {deliveryDate && (
            <div className="mt-4 p-4 bg-white rounded-xl">
              <p className="text-sm text-gray-600 mb-1">Expected Delivery Date:</p>
              <p className="text-lg font-bold text-primary">
                {format(new Date(deliveryDate), 'MMMM dd, yyyy (EEEE)')}
              </p>
            </div>
          )}
        </motion.div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6"
        >
          <h3 className="font-bold text-yellow-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
            <li>Once accepted, the order cannot be cancelled</li>
            <li>Please pack the item carefully to avoid damage</li>
            <li>Customer will be notified about the delivery date</li>
            <li>Update order status to 'Shipped' when dispatched</li>
          </ul>
        </motion.div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAccept}
            disabled={processing || !deliveryDate}
            className="flex-1 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Check size={24} />
            {processing ? 'Processing...' : 'Accept Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptOrder;